import React, { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { uuidv7 as uuid } from "uuidv7";

import { RootState } from "src/redux/reducers";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SettingsButtons } from "src/app/SettingsModal/components/SettingsTabContent/SettingsTabContent";
import { ChangeSettingsSectionParam } from "src/app/SettingsModal/methods";
import { ScalingInput } from "src/app/components/ScalingInput/ScalingInput";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";

import {
  addCreatorContentMonitor,
  clearNewlyAddedMonitorUuid,
  getCreatorContentMonitoringList,
  updateCreatorContentMonitor,
} from "src/redux";
import { useObjectState } from "src/hooks/useObjectState";
import { CCMonitorState } from "src/types";
import { useParams } from "react-router";
import { translateMessage } from "src/app/methods/translateMessage";
import { useIntl } from "react-intl";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import _ from "lodash";
import { Button } from "src/app/components/Button/Button";
import { ExtensionsSection } from "../../../Extensions";
import ExtensionView from "../../../ExtensionView";
import { CCMNotifications } from "./tabs/CCMNotifications/CCMNotifications";
import { CCMGeneral } from "./tabs/CCMGeneral/CCMGeneral";

import "./CreatorContentMonitoringDetails.scss";

enum ActiveTabs {
  General = "general",
  Notifications = "notifications",
}

interface Props {
  onClose: () => void;
  setExtensionsSection: (section: ChangeSettingsSectionParam) => void;
  closeDetailsView: () => void;
}

export const CreatorContentMonitoringDetails = (props: Props) => {
  const { onClose, setExtensionsSection, closeDetailsView } = props;

  const [activeTab, setActiveTab] = useState(ActiveTabs.General);
  const [enabled, setEnabled] = useState(false);
  const [monitorData, setMonitorData] = useObjectState<CCMonitorState>({
    name: "",
    startDate: null,
    endDate: null,
    socialPlatforms: [],
    keywords: [],
    keywordsMatchType: { value: "or" },
    notificationIcon: false,
    notificationWsMembers: [],
    metrics: [],
  });
  const [loading, setLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { creatorContentMonitors, newlyAddedMonitorUuid } = useSelector(
    (state: RootState) => state.extensionReducer,
  );
  const { settingsModalData } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { objectUuid } = settingsModalData;

  const dispatch = useDispatch();
  const intl = useIntl();

  const { projectId } = useParams<{ projectId: string }>();

  const editedElement = creatorContentMonitors.find(
    (item) => item.uuid === objectUuid,
  );

  useEffect(() => {
    if (objectUuid === "create-new") {
      inputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!newlyAddedMonitorUuid) return;
    dispatch(clearNewlyAddedMonitorUuid());
    dispatch(getCreatorContentMonitoringList(projectId));
    closeDetailsView();
  }, [newlyAddedMonitorUuid]);

  useEffect(() => {
    if (!editedElement) return;
    setEnabled(editedElement.enabled);
    setMonitorData({
      name: editedElement.name,
      startDate: editedElement.startDate,
      endDate: editedElement.endDate,
      socialPlatforms: editedElement.socialPlatforms,
      keywords: editedElement.keywords,
      keywordsMatchType: { value: editedElement.keywordsMatchType },
      notificationIcon: editedElement.notificationIcon,
      notificationWsMembers: editedElement.notificationWsMembers || [],
      metrics: editedElement.metrics || [],
    });
  }, [editedElement]);

  const shouldMonitorBeUpdated = () => {
    if (!editedElement) return false;

    const nameChanged = editedElement.name !== monitorData.name;
    const enabledChanged = editedElement.enabled !== enabled;
    const startDateChanged = editedElement.startDate !== monitorData.startDate;
    const endDateChanged = editedElement.endDate !== monitorData.endDate;
    const socialPlatformsChanged = !_.isEqual(
      editedElement.socialPlatforms,
      monitorData.socialPlatforms,
    );
    const keywordsChanged = !_.isEqual(
      editedElement.keywords,
      monitorData.keywords,
    );
    const keywordsMatchTypeChanged =
      editedElement.keywordsMatchType !== monitorData.keywordsMatchType.value;
    const membersChanged = !_.isEqual(
      editedElement.notificationWsMembers,
      monitorData.notificationWsMembers,
    );
    const metricsChanged = !_.isEqual(
      editedElement.metrics,
      monitorData.metrics,
    );

    const changes = [
      nameChanged,
      enabledChanged,
      startDateChanged,
      endDateChanged,
      socialPlatformsChanged,
      keywordsChanged,
      keywordsMatchTypeChanged,
      membersChanged,
      metricsChanged,
    ];

    return changes.some((con: boolean) => con);
  };

  const updateMonitor = async () => {
    if (!editedElement) return;

    setLoading(true);

    const {
      name,
      startDate,
      endDate,
      socialPlatforms,
      keywords,
      keywordsMatchType,
      notificationIcon,
      notificationWsMembers,
      metrics,
    } = monitorData;

    await dispatch(
      updateCreatorContentMonitor({
        name,
        uuid: editedElement.uuid,
        wsProjectUuid: projectId,
        startDate,
        endDate,
        socialPlatforms,
        keywords,
        keywordsMatchType: keywordsMatchType?.value,
        notificationIcon,
        enabled,
        notificationWsMembers,
        metrics,
      }),
    );
    setLoading(false);
  };

  const onSwitchChange = () => {
    setEnabled((v) => !v);
  };

  const createNewMonitor = async () => {
    const monitorUuid = uuid();

    const {
      name,
      startDate,
      endDate,
      socialPlatforms,
      keywords,
      keywordsMatchType,
      notificationIcon,
      notificationWsMembers,
      metrics,
    } = monitorData;

    setLoading(true);
    await dispatch(
      addCreatorContentMonitor({
        name,
        uuid: monitorUuid,
        wsProjectUuid: projectId,
        startDate,
        endDate,
        socialPlatforms,
        keywords,
        keywordsMatchType: keywordsMatchType?.value,
        notificationIcon,
        enabled: true,
        notificationWsMembers,
        metrics,
      }),
    );
    setLoading(false);
  };

  const renderTab = () => {
    switch (activeTab) {
      case ActiveTabs.General:
        return (
          <CCMGeneral
            monitorData={monitorData}
            setMonitorData={setMonitorData}
          />
        );
      case ActiveTabs.Notifications:
        return (
          <CCMNotifications
            monitorData={monitorData}
            setMonitorData={setMonitorData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ExtensionView
      navigationButtons={[
        {
          label: (
            <IDHFormattedMessage
              id="ws_extensions"
              defaultMessage="Extensions"
            />
          ),
          action: () => setExtensionsSection(ExtensionsSection.ExtensionsList),
        },
        {
          label: (
            <IDHFormattedMessage
              id="ws_creator_content_monitoring"
              defaultMessage="Creator content monitoring"
            />
          ),
          action: closeDetailsView,
        },
      ]}
      title={
        <>
          <ScalingInput
            ref={inputRef}
            className="preview__info-title"
            value={monitorData.name}
            onChange={(e) => setMonitorData({ name: e.target.value })}
            placeholder={translateMessage({
              intl,
              id: "ws_enter_detection_name",
              defaultMessage: "Enter detection name",
            })}
          />
          <CustomSwitch
            checked={enabled}
            onChange={onSwitchChange}
            disabled={monitorData.name.length < 2}
          />
        </>
      }
    >
      <div className="creator-content-monitoring-details">
        <div className="creator-content-monitoring-details__content">
          <Tabs>
            <Tab
              tabText={
                <IDHFormattedMessage id="ws_general" defaultMessage="General" />
              }
              active={activeTab === ActiveTabs.General}
              onClick={() => setActiveTab(ActiveTabs.General)}
            />
            <Tab
              tabText={
                <IDHFormattedMessage
                  id="ws_notifications"
                  defaultMessage="Notifications"
                />
              }
              active={activeTab === ActiveTabs.Notifications}
              onClick={() => setActiveTab(ActiveTabs.Notifications)}
            />
          </Tabs>

          <div className="creator-content-monitoring-details__content-tab-content">
            {renderTab()}
          </div>
        </div>

        <SettingsButtons onClose={onClose}>
          {editedElement ? (
            <Button
              size="large"
              variant="blue"
              onClick={updateMonitor}
              disabled={!shouldMonitorBeUpdated() || loading}
              data-qa="update-monitor-button"
            >
              <IDHFormattedMessage id="ws_update" defaultMessage="Update" />
            </Button>
          ) : (
            <Button
              size="large"
              variant="blue"
              onClick={createNewMonitor}
              disabled={loading}
              data-qa="create-new-monitor-button"
            >
              <IDHFormattedMessage id="ws_create" defaultMessage="Create" />
            </Button>
          )}
        </SettingsButtons>
      </div>
    </ExtensionView>
  );
};
